import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const ST = ['open','investigating','root_cause_identified','resolved','closed'];
const stLabel = s => ({ open:'Open', investigating:'Investigating', root_cause_identified:'Root cause found', resolved:'Resolved', closed:'Closed' }[s] || s);
const stColor = s => ({ open:'var(--orange)', investigating:'var(--accent)', root_cause_identified:'#B4A7D6', resolved:'var(--green)', closed:'var(--text2)' }[s] || 'var(--text2)');
const kindLabel = k => ({ asset:'🖥 Failing asset', room:'🚪 Recurring room issue', category:'🔁 Repeated category' }[k] || k);

export default function Problems() {
  const [signals, setSignals] = useState([]);
  const [problems, setProblems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => {
    api.get('/problems/signals').then(r => setSignals(r.data.signals)).catch(()=>{});
    api.get('/problems').then(r => setProblems(r.data)).catch(()=>{});
  }, []);
  useEffect(() => { load(); }, [load]);
  const flash = m => { setMsg(m); setTimeout(()=>setMsg(''),3000); };

  const createFromSignal = async sig => {
    const title = window.prompt('Problem title:', signalTitle(sig));
    if (!title) return;
    try {
      const r = await api.post('/problems', { title, from_signal:true,
        asset_id:sig.asset_id||null, room_id:sig.room_id||null, category_id:sig.category_id||null, campus_id:sig.campus_id||null });
      flash(`✓ Problem created — ${r.data.linked} task(s) linked`); load();
    } catch(e){ flash('Failed'); }
  };

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>🧩 Risk &amp; Problem Management</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Recurring faults detected from the last 90 days. Raise a Problem Record to fix the root cause instead of repeating the fix.</p>
      </div>
      {msg && <div style={{ fontSize:13, marginBottom:10, color: msg.startsWith('✓')?'var(--green)':'var(--red)' }}>{msg}</div>}

      <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>Detected signals ({signals.length})</div>
      {!signals.length ? <div className="card"><div className="empty-state"><p>No recurring patterns (≥3 in 90 days). Good sign.</p></div></div> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:12, marginBottom:24 }}>
          {signals.map((s,i) => (
            <div key={i} className="card" style={{ padding:14, borderLeft:'3px solid var(--orange)' }}>
              <div style={{ fontSize:11, color:'var(--text2)', fontWeight:600, textTransform:'uppercase' }}>{kindLabel(s.kind)}</div>
              <div style={{ fontWeight:600, marginTop:2 }}>{signalTitle(s)}</div>
              <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>{s.campus_name || 'all campuses'} · last {new Date(s.last_seen).toLocaleDateString()}</div>
              <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginTop:10 }}>
                <span style={{ fontSize:20, fontWeight:700, color:'var(--orange)' }}>{s.n}<span style={{ fontSize:12, color:'var(--text2)', fontWeight:400 }}> occurrences</span></span>
                <button className="btn btn-primary btn-sm" onClick={() => createFromSignal(s)}>Create Problem</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>Problem records ({problems.length})</div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!problems.length ? <div className="empty-state"><p>No problem records yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Title</th><th>Scope</th><th>Linked</th><th>Status</th><th>Created</th></tr></thead>
            <tbody>{problems.map(p => (
              <tr key={p.id} style={{ cursor:'pointer', opacity:['resolved','closed'].includes(p.status)?0.6:1 }} onClick={() => setOpenId(p.id)}>
                <td style={{ fontWeight:500 }}>{p.title}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{[p.campus_name,p.category_name,p.asset_tag,p.room_name].filter(Boolean).join(' · ')||'—'}</td>
                <td>{p.linked_tasks}</td>
                <td><span style={{ fontSize:11, fontWeight:600, color:stColor(p.status) }}>{stLabel(p.status)}</span></td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{new Date(p.created_at).toLocaleDateString()}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {openId && <ProblemDetail id={openId} onClose={() => { setOpenId(null); load(); }} />}
    </div>
  );
}

function signalTitle(s) {
  if (s.kind === 'asset')    return `${s.asset_tag || 'Asset'} failing repeatedly`;
  if (s.kind === 'room')     return `${s.room_name || 'Room'} — recurring ${s.category_name || 'issue'}`;
  return `Repeated ${s.category_name || 'issue'} at ${s.campus_name || 'campus'}`;
}

function ProblemDetail({ id, onClose }) {
  const [p, setP] = useState(null);
  const load = useCallback(() => api.get(`/problems/${id}`).then(r => setP(r.data)).catch(()=>{}), [id]);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;
  const upd = async patch => { try { await api.put(`/problems/${id}`, patch); load(); } catch(e){} };
  const detach = async tid => { await api.post(`/problems/${id}/link`, { task_id:tid, detach:true }); load(); };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:700, maxHeight:'92vh', overflowY:'auto' }}>
        <div className="modal-header"><span className="modal-title">🧩 {p.title}</span><button className="modal-close" onClick={onClose}>×</button></div>
        <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>{[p.campus_name,p.category_name,p.asset_tag,p.room_name].filter(Boolean).join(' · ')||'—'}</div>

        <div className="form-group"><label>Status</label>
          <select value={p.status} onChange={e => upd({ status:e.target.value })} style={{ maxWidth:240 }}>
            {ST.map(s => <option key={s} value={s}>{stLabel(s)}</option>)}
          </select></div>
        <div className="form-group"><label>Description</label>
          <textarea rows={2} defaultValue={p.description||''} onBlur={e => e.target.value!==(p.description||'') && upd({ description:e.target.value })} /></div>
        <div className="form-group"><label>Root cause</label>
          <textarea rows={2} defaultValue={p.root_cause||''} onBlur={e => e.target.value!==(p.root_cause||'') && upd({ root_cause:e.target.value })} placeholder="What is actually causing the repeats?" /></div>
        <div className="form-group"><label>Permanent fix</label>
          <textarea rows={2} defaultValue={p.permanent_fix||''} onBlur={e => e.target.value!==(p.permanent_fix||'') && upd({ permanent_fix:e.target.value })} placeholder="The lasting solution applied" /></div>

        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'6px 0' }}>LINKED TASKS ({p.tasks.length})</div>
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          {!p.tasks.length ? <div style={{ fontSize:12, color:'var(--text2)', padding:10 }}>No tasks linked.</div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Task</th><th>Title</th><th>Campus</th><th>Status</th><th></th></tr></thead>
              <tbody>{p.tasks.map(t => (
                <tr key={t.id}><td className="mono" style={{ fontSize:12 }}>{t.task_id}</td><td style={{ fontSize:12 }}>{t.title}</td>
                  <td style={{ fontSize:12 }}>{t.campus_name}</td><td style={{ fontSize:12 }}>{t.status}</td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => detach(t.id)}>Unlink</button></td></tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>
    </div>
  );
}
